import React from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';
import { Delete as DeleteIcon, Edit } from '@mui/icons-material';
import styles from './TodoSetting.module.scss';
import type { ITodoCategory } from 'core/API/todo';

interface TodoCategoriesProps {
    categoriesData: ITodoCategory[];
    loading: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
    canExecute?: boolean;
    onCreateClick: () => void;
    onEditClick: (category: ITodoCategory) => void;
    onDeleteCategoryClick: (category: ITodoCategory) => void;
}

const TodoCategories: React.FC<TodoCategoriesProps> = ({
    categoriesData,
    loading,
    canEdit = false,
    canDelete = false,
    canExecute = false,
    onCreateClick,
    onEditClick,
    onDeleteCategoryClick,
}) => {
    return (
        <div className={styles.categoriesPanel}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Todo Categories</Typography>
                <Button
                    className={styles.button}
                    onClick={onCreateClick}
                    disabled={!canExecute}
                    title={canExecute ? 'Add category' : 'You do not have permission to add'}
                    style={{
                        cursor: canExecute ? 'pointer' : 'not-allowed',
                        opacity: canExecute ? 1 : 0.6,
                    }}
                >
                    Add
                </Button>
            </Box>

            <div className={styles.tableContainer}>
                <table className={styles.workplaceTable}>
                    <thead>
                        <tr>
                            <th>Category Name</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Edit</th>
                            <th style={{ padding: '8px', width: '48px', textAlign: 'center' }}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', padding: '40px' }}>
                                    <CircularProgress size={20} color="inherit" />
                                </td>
                            </tr>
                        ) : categoriesData.length !== 0 ? (
                            categoriesData
                                .map((category) => (
                                    <tr key={category.id}>
                                        <td>{category.label}</td>
                                        <td
                                            style={{
                                                textAlign: 'center',
                                                padding: '8px',
                                                width: 48,
                                            }}
                                        >
                                            <button
                                                className={styles.iconButton}
                                                onClick={() => onEditClick(category)}
                                                title={
                                                    canEdit
                                                        ? 'Edit'
                                                        : 'You do not have permission to edit'
                                                }
                                                disabled={!canEdit}
                                                style={{
                                                    opacity: canEdit ? 1 : 0.5,
                                                    cursor: canEdit ? 'pointer' : 'not-allowed',
                                                }}
                                            >
                                                <Edit className={styles.editIcon} />
                                            </button>
                                        </td>
                                        <td
                                            style={{
                                                textAlign: 'center',
                                                padding: '8px',
                                                width: 48,
                                            }}
                                        >
                                            {categoriesData.length > 1 ? (
                                                <button
                                                    className={styles.iconButton}
                                                    onClick={() => onDeleteCategoryClick(category)}
                                                    title={
                                                        canDelete
                                                            ? 'Delete'
                                                            : 'You do not have permission to delete'
                                                    }
                                                    disabled={!canDelete}
                                                    style={{
                                                        opacity: canDelete ? 1 : 0.5,
                                                        cursor: canDelete
                                                            ? 'pointer'
                                                            : 'not-allowed',
                                                    }}
                                                >
                                                    <DeleteIcon className={styles.deleteIcon} />
                                                </button>
                                            ) : (
                                                <Tooltip title="Cannot delete the last category">
                                                    <button
                                                        className={styles.iconButton}
                                                        disabled
                                                        style={{
                                                            opacity: 0.5,
                                                            cursor: 'not-allowed',
                                                        }}
                                                    >
                                                        <DeleteIcon className={styles.deleteIcon} />
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </td>
                                    </tr>
                                ))
                                .reverse()
                        ) : (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', padding: '20px' }}>
                                    No records found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TodoCategories;
